// Columna izquierda del explorer: Búsqueda · Estado MDM · Atención · Proveedor · Señal · Marca.
// La búsqueda se emite con debounce; el resto viaja al padre en el click.
import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import type { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { cn } from '@/lib/cn';
import {
    countActiveFilters,
    fleetStateLabel,
    MDM_FLEET_STATE_ORDER,
    MDM_PROVIDER_LABEL,
} from '../logic';
import {
    ANY_FILTER,
    EMPTY_FLEET_FILTERS,
    type HeartbeatBucket,
    type MdmFleetFilters,
} from '../types';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

/** Los estados de `readFleetState` en el orden del ciclo de vida: los mismos que cuentan los KPIs. */
const STATE_OPTIONS = MDM_FLEET_STATE_ORDER.map((state) => ({
    value: state,
    label: fleetStateLabel(state),
}));

const HEARTBEAT_OPTIONS: { value: HeartbeatBucket; label: string }[] = [
    { value: 'up-to-date', label: 'Al día (<24h)' },
    { value: 'recent', label: '1–3 días' },
    { value: 'stale', label: '4–7 días' },
    { value: 'critical', label: 'Más de 7 días' },
];

export interface MdmFiltersPanelProps {
    filters: MdmFleetFilters;
    onChange: (next: MdmFleetFilters) => void;
    /** Marcas del fleet, ya en orden alfabético (`listBrands`). */
    brands: readonly string[];
    /** Proveedores presentes en el fleet, ya en orden alfabético (`listProviders`). */
    providers: readonly MdmProviderEnum[];
    /** Que los filtros miran lo cargado y no la flota. `null` cuando ya no quedan páginas. */
    loadedNote?: string | null;
    /** Cómo se está buscando cuando la búsqueda va al servidor. `null` = busca en lo cargado. */
    searchNote?: string | null;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function MdmFiltersPanel({
    filters,
    onChange,
    brands,
    providers,
    loadedNote = null,
    searchNote = null,
    collapsed,
    onToggleCollapsed,
}: MdmFiltersPanelProps) {
    const [searchText, setSearchText] = useState(filters.search);
    const debouncedSearch = useDebounce(searchText, 250);
    const emittedSearch = useRef(filters.search);

    // Sube la búsqueda ya reposada; el guard evita re-emitir cuando cambia otro filtro.
    useEffect(() => {
        if (debouncedSearch === emittedSearch.current) return;
        emittedSearch.current = debouncedSearch;
        onChange({ ...filters, search: debouncedSearch });
    }, [debouncedSearch, filters, onChange]);

    // Baja un reset hecho desde afuera (cambio de proveedor, limpiar del padre) a la caja local.
    useEffect(() => {
        if (filters.search === emittedSearch.current) return;
        emittedSearch.current = filters.search;
        setSearchText(filters.search);
    }, [filters.search]);

    const patch = (partial: Partial<MdmFleetFilters>) => {
        onChange({ ...filters, ...partial });
    };

    const clearSearch = () => {
        emittedSearch.current = '';
        setSearchText('');
        patch({ search: '' });
    };

    const clearAll = () => {
        emittedSearch.current = '';
        setSearchText('');
        onChange(EMPTY_FLEET_FILTERS);
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={clearAll}
            titleAdornment={
                loadedNote === null ? null : (
                    <span className="rounded bg-warning/15 px-1 py-px text-[9px] font-bold uppercase tracking-[0.06em] text-foreground">
                        Sobre lo cargado
                    </span>
                )
            }
        >
            {loadedNote === null ? null : (
                <div
                    role="status"
                    className="mt-2 rounded border border-border bg-muted/40 px-2 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {loadedNote}
                </div>
            )}

            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="text"
                        value={searchText}
                        onChange={(event) => {
                            setSearchText(event.target.value);
                        }}
                        // `CL-…` es el placeholder literal del mock; el expediente MDM todavía
                        // no guarda al cliente, así que hoy esa clave no devuelve nada.
                        placeholder="IMEI, CR-…, CL-…"
                        aria-label="Buscar por IMEI, crédito o SKU"
                        className={cn(FIELD, 'pl-6 pr-6')}
                    />
                    {searchText === '' ? null : (
                        <button
                            type="button"
                            onClick={clearSearch}
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    )}
                </div>
                {searchNote === null ? null : (
                    <p
                        role="status"
                        className="mt-1 text-[10px] leading-snug text-muted-foreground"
                    >
                        {searchNote}
                    </p>
                )}
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Estado MDM</div>
                <select
                    value={filters.state}
                    onChange={(event) => {
                        patch({ state: event.target.value as MdmFleetFilters['state'] });
                    }}
                    aria-label="Estado MDM"
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {STATE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Atención</div>
                <select
                    value={filters.attention}
                    onChange={(event) => {
                        patch({
                            attention: event.target.value as MdmFleetFilters['attention'],
                        });
                    }}
                    aria-label="Atención"
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    <option value="needed">Requiere atención</option>
                </select>
            </div>

            {/* Con un solo proveedor en la flota el combo no distingue nada: no se pinta. */}
            {providers.length < 2 ? null : (
                <div className={GROUP}>
                    <div className={LABEL}>Proveedor</div>
                    <select
                        value={filters.provider}
                        onChange={(event) => {
                            patch({
                                provider: event.target.value as MdmFleetFilters['provider'],
                            });
                        }}
                        aria-label="Proveedor"
                        className={FIELD}
                    >
                        <option value={ANY_FILTER}>Todos</option>
                        {providers.map((provider) => (
                            <option key={provider} value={provider}>
                                {MDM_PROVIDER_LABEL[provider]}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className={GROUP}>
                <div className={LABEL}>Señal del equipo</div>
                <select
                    value={filters.heartbeat}
                    onChange={(event) => {
                        patch({
                            heartbeat: event.target.value as MdmFleetFilters['heartbeat'],
                        });
                    }}
                    aria-label="Señal del equipo"
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {HEARTBEAT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Sin marcas en la flota el combo solo tendría «Todas»: un control que no filtra nada. */}
            {brands.length === 0 ? null : (
                <div className={GROUP}>
                    <div className={LABEL}>Marca</div>
                    <select
                        value={filters.brand}
                        onChange={(event) => {
                            patch({ brand: event.target.value });
                        }}
                        aria-label="Marca"
                        className={FIELD}
                    >
                        <option value={ANY_FILTER}>Todas</option>
                        {brands.map((brand) => (
                            <option key={brand} value={brand}>
                                {brand}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </FiltersColumn>
    );
}
